import {
  Box,
  Button,
  Typography,
  Pagination,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import DashboardLayout from "../../layout";
import AppTable from "../Dashboard/Table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Booking,
  FlightSearchParams,
  getTransactions,
  mapFlightSearchRecordToBooking,
  searchFlight,
} from "../../services/manualBooking";
import { LoadingState } from "../../components/LoadingState";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import BookingDetailsPanel from "./BookingDetailsPanel";
import { useToast } from "../../components/Toast";

const getAirportCode = (value?: string) => {
  if (!value) return "N/A";
  const parts = value.split(" - ");
  return parts.length > 1 ? parts[parts.length - 1].trim() : value.trim();
};

const statusColor = (status: string) => {
  const value = (status || "").toLowerCase();
  if (
    [
      "successful",
      "success",
      "confirmed",
      "booked_successfully",
      "completed",
    ].includes(value)
  )
    return "green";
  if (value === "pending") return "orange";
  return "red";
};

const SEARCH_FIELDS: { value: keyof FlightSearchParams; label: string }[] = [
  { value: "pnr", label: "PNR" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "passengerName", label: "Passenger Name" },
  { value: "origin", label: "Origin" },
  { value: "destination", label: "Destination" },
  { value: "travelDate", label: "Travel Date" },
];

const ManualBookings = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  // Keep only the snapshot as a fallback; the panel reads the live copy from
  // the (re)fetched list below so it never shows pre-reschedule data.
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [searchField, setSearchField] =
    useState<keyof FlightSearchParams>("pnr");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState<FlightSearchParams | null>(
    null,
  );
  const [searching, setSearching] = useState(false);
  const limit = 10;

  const { data: transactionsResponse, isLoading } = useQuery({
    queryKey: ["transactions", page],
    queryFn: () => getTransactions(page, limit),
  });

  // Under the "transactions" prefix so the drawers' invalidation after a
  // reschedule/cancel refreshes search results too.
  const searchKey = (params: FlightSearchParams | null) => [
    "transactions",
    "search",
    params,
  ];
  const { data: searchResponse } = useQuery({
    queryKey: searchKey(activeSearch),
    queryFn: () => searchFlight(activeSearch!),
    enabled: Boolean(activeSearch),
  });

  const searchResults = useMemo<Booking[] | null>(() => {
    if (!activeSearch || !searchResponse) return null;
    if (!searchResponse.success) return [];
    return (searchResponse.data?.flightBookingData || []).map(
      mapFlightSearchRecordToBooking,
    );
  }, [activeSearch, searchResponse]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const params = { [searchField]: searchQuery } as FlightSearchParams;
    setSearching(true);
    try {
      // Every click hits the API (staleTime 0), as the old mutation did.
      const response = await queryClient.fetchQuery({
        queryKey: searchKey(params),
        queryFn: () => searchFlight(params),
        staleTime: 0,
      });
      setActiveSearch(params);
      if (
        !response?.success ||
        (response?.data?.flightBookingData || []).length === 0
      ) {
        showToast(response?.message || "No bookings found", "info");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || "Search failed. Please try again.",
        "error",
      );
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setActiveSearch(null);
    setSearchQuery("");
  };

  const transactions = [
    ...(transactionsResponse?.data?.completeBookings || []),
    ...(transactionsResponse?.data?.pendingBookings || []),
    ...(transactionsResponse?.data?.expiredBookings || []),
  ];
  const totalPages = transactionsResponse?.data?.pagination?.totalPages || 1;

  const bookingsToShow = searchResults ?? transactions;
  const liveSelectedBooking = selectedBooking
    ? bookingsToShow.find((b) => b.id === selectedBooking.id) ?? selectedBooking
    : null;

  const headers = [
    { label: "Reference", key: "reference" },
    { label: "Passenger", key: "passenger" },
    { label: "Airline", key: "airline" },
    { label: "PNR", key: "pnr" },
    { label: "Route", key: "route" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
  ];

  const tableData = bookingsToShow.map((booking) => {
    const passengerName = booking.passengers
      ?.map(
        (passenger) =>
          passenger.fullName ||
          `${passenger.firstName || ""} ${passenger.lastName || ""}`.trim(),
      )
      .filter(Boolean)
      .join(", ");
    const bookingDate = new Date(booking.date);
    const hasValidDate = !isNaN(bookingDate.getTime());

    return {
      reference: booking.booking_id,
      passenger: (
        <Box display="flex" flexDirection="column">
          <Typography fontSize={12} fontWeight={600} color="#101828">
            {passengerName || "N/A"}
          </Typography>
          <Typography fontSize={11} color="#667085">
            {booking.contact || booking.email || "N/A"}
          </Typography>
        </Box>
      ),
      airline: booking.airline || "N/A",
      pnr: booking.pnr || "N/A",
      route: (
        <Box display="flex" flexDirection="column" gap="2px">
          <Box display="flex" alignItems="center" gap="4px">
            <FlightTakeoffIcon sx={{ fontSize: 13, color: "#667085" }} />
            <Typography fontSize={12} fontWeight={600} color="#101828">
              {getAirportCode(booking.from)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap="4px">
            <FlightLandIcon sx={{ fontSize: 13, color: "#667085" }} />
            <Typography fontSize={12} fontWeight={600} color="#101828">
              {getAirportCode(booking.to)}
            </Typography>
          </Box>
        </Box>
      ),
      amount: `₦${Number(booking.amount).toLocaleString()}`,
      status: (
        <Typography
          sx={{
            color: statusColor(booking.status),
            fontWeight: 600,
            fontSize: "12px",
            textTransform: "capitalize",
          }}
        >
          {booking.status}
        </Typography>
      ),
      date: (
        <Box display="flex" flexDirection="column">
          <Typography fontSize={12} fontWeight={600} color="#101828">
            {hasValidDate ? bookingDate.toLocaleDateString() : "N/A"}
          </Typography>
          <Typography fontSize={11} color="#667085">
            {hasValidDate ? bookingDate.toLocaleTimeString() : ""}
          </Typography>
        </Box>
      ),
    };
  });

  const handleRowClick = (row: { [key: string]: string | JSX.Element }) => {
    const booking = bookingsToShow.find(
      (booking) => booking.booking_id === row.reference,
    );
    if (booking) setSelectedBooking(booking);
  };

  return (
    <DashboardLayout nextSideBar>
      {(isLoading || searching) && <LoadingState />}
      <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
          gap={{ xs: 1, sm: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600} color="#101828">
              Bookings
            </Typography>
            <Typography variant="body2" color="#475467">
              Manage your bookings and view transactions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/dashboard/manual-bookings/add")}
            sx={{
              bgcolor: "#0b1c12",
              "&:hover": { bgcolor: "#08150e" },
            }}
          >
            Add Manual Booking
          </Button>
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={2}
        >
          <Box
            display="flex"
            gap={{ xs: 1, sm: 2 }}
            width={{ xs: "100%", sm: "auto" }}
          >
            <Select
              size="small"
              value={searchField}
              onChange={(e) =>
                setSearchField(e.target.value as keyof FlightSearchParams)
              }
              sx={{ width: 180, bgcolor: "#FFFFFF" }}
            >
              {SEARCH_FIELDS.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder={`Search by ${
                SEARCH_FIELDS.find((field) => field.value === searchField)
                  ?.label
              }...`}
              type={searchField === "travelDate" ? "date" : "text"}
              InputLabelProps={
                searchField === "travelDate" ? { shrink: true } : undefined
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{ width: 260, bgcolor: "#FFFFFF" }}
            />
          </Box>
          <Box>
            <Button
              variant="contained"
              disabled={!searchQuery.trim() || searching}
              onClick={handleSearch}
              sx={{
                bgcolor: "#0b1c12",
                "&:hover": { bgcolor: "#08150e" },
              }}
            >
              {searching ? "Searching..." : "Search"}
            </Button>
            {searchResults && (
              <Button variant="text" onClick={handleClearSearch}>
                Clear Search
              </Button>
            )}
          </Box>
        </Box>

        {searchResults && (
          <Typography fontSize="14px" color="#475467" mb={2}>
            {searchResults.length} result{searchResults.length === 1 ? "" : "s"}{" "}
            found
          </Typography>
        )}

        <AppTable headers={headers} data={tableData} onClick={handleRowClick} />

        {!searchResults && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Box>

      <BookingDetailsPanel
        open={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        booking={liveSelectedBooking}
      />
    </DashboardLayout>
  );
};

export default ManualBookings;
